import { useState, useEffect, useMemo } from "react";
import {
  Globe,
  Sun,
  Moon,
  Clock,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Star,
} from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSurveyLibraryStore } from "../hooks/useSurveyLibraryStore";
import { useResponseSurveyStore } from "../hooks/useResponseSurveyStore";
import { useAuthStore } from "@/features/auth/hooks";
import { formatDuration } from "../utils/survey-manager.helper";
import QuestionInput from "../components/QuestionInput";
import { FormField } from "@/components/common";
import { observer } from "mobx-react-lite";
import { useTheme } from "@/hooks";

// Constants
const STEP = {
  LANDING: "landing",
  DETAILS: "details",
  QUESTION: "question",
  DONE: "done",
};
const SHARE_LINK = {
  message:
    "This survey collects essential company and sustainability information to assess your organisation’s current practices, carbon data readiness, and sustainability development needs.",
};
// ─── Helper to extract value from event OR plain string ───
const extractValue = (v) =>
  v && typeof v === "object" && v.target ? v.target.value : (v ?? "");
// Shared: Top Header
const TopHeader = ({ isDark, onToggleDark }) => (
  <header
    className="flex-shrink-0 flex items-center justify-between px-8 py-4 
        border-b border-gray-100 dark:border-gray-800 
        bg-white dark:bg-gray-950 z-10"
  >
    <span className="text-base font-bold text-emerald-600 tracking-wide">
      NXSTART
    </span>
    <div className="flex items-center gap-4 text-sm text-gray-400 dark:text-gray-500">
      <button className="flex items-center gap-1.5 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
        <Globe className="h-4 w-4" />
        <span>English</span>
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {/* ✅ Toggle dark/light */}
      <button
        onClick={onToggleDark}
        className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
      </button>
    </div>
  </header>
);

const ProgressBar = ({ current, total, surveyName }) => {
  const pct = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  return (
    <div
      className="flex-shrink-0 bg-white dark:bg-gray-950 
            border-b border-gray-100 dark:border-gray-800"
    >
      <div className="max-w-2xl mx-auto px-6 pt-4 pb-3">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate pr-4">
            {surveyName}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
            {current + 1} of {total} ({pct}%)
          </span>
        </div>
        <div className="h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
};
// Shared: Powered-by footer
const PoweredBy = () => (
  <p className="mt-12 text-center text-xs text-gray-400 dark:text-gray-500">
    Powered by NxStart
  </p>
);

/** Returns true when a required question has a meaningful answer */
const hasAnswer = (question, value) => {
  if (!question.required) return true;
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;

  // ✅ Email format validation
  if (question.questionType === "email") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // ✅ Number range validation
  if (question.questionType === "number") {
    const num = Number(value);
    if (isNaN(num)) return false;
    if (question.min !== undefined && num < question.min) return false;
    if (question.max !== undefined && num > question.max) return false;
  }

  return true;
};
// Step 1: Landing
const LandingPage = ({ survey, shareLink, inviter, questions, onStart }) => (
  <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
    <div className="max-w-2xl mx-auto w-full px-6 py-10">
      {(inviter?.name || inviter?.company) && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Shared by{" "}
          {inviter.name && (
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {inviter.name}
            </span>
          )}
          {inviter.name && inviter.company && ", "}
          {inviter.company && (
            <span className="text-gray-600 dark:text-gray-400">
              {inviter.company}
            </span>
          )}
        </p>
      )}

      <h1 className="text-[1.85rem] font-bold text-gray-900 dark:text-gray-100 mb-4 leading-tight">
        {survey?.name}
      </h1>

      {survey?.description && (
        <div className="flex mb-4">
          <div className="w-1 rounded-full bg-emerald-500 flex-shrink-0 mr-4" />
          <div className="bg-gray-50 dark:bg-gray-900 rounded-r-xl px-5 py-3.5 flex-1">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {survey.description}
            </p>
          </div>
        </div>
      )}

      {shareLink?.message && (
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
          {shareLink?.message}
        </p>
      )}

      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
        <Clock className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <span>
          About {survey?.estimatedTime || "5 minutes"}
          {questions.length > 0 && (
            <>
              {" "}
              · {questions.length} question{questions.length !== 1 ? "s" : ""}
            </>
          )}
        </span>
      </div>

      <button
        onClick={onStart}
        className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800
                           text-white font-semibold px-10 py-3 rounded-lg text-sm transition-colors"
      >
        Start {survey?.type === "assessment" ? "assessment" : "survey"}
      </button>

      {inviter?.name && (
        <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">
          Prefer to speak with us directly?{" "}
          <a
            href={inviter?.email ? `mailto:${inviter.email}` : "#"}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Contact {inviter.name.split(" ")[0]}
          </a>
        </p>
      )}

      <p className="mt-8 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
        Your responses are shared with{" "}
        <span className="text-gray-500 dark:text-gray-400">
          {inviter?.company || "the survey creator"}
        </span>{" "}
        and processed by NxStart.{" "}
        <button className="text-emerald-600 hover:underline font-medium">
          Learn more
        </button>
      </p>

      <PoweredBy />
    </div>
  </div>
);

const DetailsPage = ({
  inviter,
  email: prefillEmail,
  form,
  setForm,
  onBack,
  onContinue,
}) => {
  // ✅ Safe string check
  const canContinue =
    typeof form.name === "string" && form.name.trim().length > 0;

  return (
    <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
      <div className="max-w-2xl mx-auto w-full px-6 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm 
                text-gray-500 dark:text-gray-400 
                hover:text-gray-700 dark:hover:text-gray-200 
                transition-colors mb-6"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            A few details before we start
          </h1>
          <p className="mt-1.5 text-sm text-emerald-600">
            This helps{inviter?.name ? ` ${inviter.name}` : " us"} follow up
            with your results.
          </p>
        </div>

        <div className="space-y-4">
          <FormField
            label="Full Name"
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: extractValue(v) }))} // ✅
            placeholder="e.g. Priya Kaung"
          />
          <FormField
            label="Work Email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: extractValue(v) }))} // ✅
            placeholder="e.g. priya@company.com"
            type="email"
            readOnly={!!prefillEmail}
          />

          <FormField
            label="Company Name"
            value={form.company}
            onChange={(v) =>
              setForm((f) => ({ ...f, company: extractValue(v) }))
            } // ✅
            placeholder="e.g. Greenleaf Industries"
          />
          <FormField
            label="Job title"
            value={form.jobTitle}
            onChange={(v) =>
              setForm((f) => ({ ...f, jobTitle: extractValue(v) }))
            } // ✅
            placeholder="e.g. Sustainability Manager"
          />
        </div>

        <button
          onClick={onContinue}
          disabled={!canContinue}
          className={[
            "mt-6 px-10 py-3 rounded-lg text-sm font-semibold text-white transition-colors",
            canContinue
              ? "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800"
              : "bg-emerald-600 opacity-40 cursor-not-allowed",
          ].join(" ")}
        >
          Continue
        </button>

        <PoweredBy />
      </div>
    </div>
  );
};
// Step 3: Questions (one at a time)
const QuestionPage = ({
  survey,
  questions,
  answers,
  onAnswer,
  onBack,
  onSubmit,
  submitting,
  uploadFn,
  deleteFn,
  downloadFn,
  uploadCompleteFn,
}) => {
  const [index, setIndex] = useState(0);
  const question = questions[index];
  const isFirst = index === 0;
  const isLast = index === questions.length - 1;
  const value = answers[question._id];
  const canAdvance = hasAnswer(question, value);

  const goNext = () => {
    if (isLast) onSubmit();
    else setIndex((i) => i + 1);
  };

  const goBack = () => {
    if (isFirst) onBack();
    else setIndex((i) => i - 1);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* ── Progress bar with survey name ── */}
      <ProgressBar
        current={index}
        total={questions.length}
        surveyName={survey?.name}
      />

      {/* ── Scrollable question area ── */}
      <div
        className="flex-1 overflow-y-auto dark:bg-gray-950"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="max-w-2xl mx-auto w-full px-6 py-10">
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-4">
            Question {index + 1}
            {!question.required && (
              <span className="ml-2 text-gray-400 dark:text-gray-500 normal-case tracking-normal font-normal">
                — optional
              </span>
            )}
          </p>

          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug">
            {question.questionText}
          </h2>

          <QuestionInput
            question={question}
            value={value}
            onChange={(v) => onAnswer(question._id, v)}
            uploadFn={uploadFn}
            deleteFn={deleteFn}
            downloadFn={downloadFn}
            uploadCompleteFn={uploadCompleteFn}
          />
        </div>
      </div>
      {/* ── Sticky bottom navigation ── */}
      <div className="flex-shrink-0 bg-white   dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 px-8 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {/* ── Previous ── */}
          <button
            onClick={goBack}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-semibold
                border border-gray-200 dark:border-gray-700
                bg-white dark:bg-gray-900
                text-gray-600 dark:text-gray-300
                hover:border-gray-300 dark:hover:border-gray-600
                hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            {!question.required && !isLast && (
              <button
                onClick={() => setIndex((i) => i + 1)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium 
                        text-gray-400 dark:text-gray-500
                        border border-gray-200 dark:border-gray-700
                        hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              >
                Skip
              </button>
            )}

            <button
              onClick={goNext}
              disabled={!canAdvance || submitting}
              className={[
                "flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-semibold",
                "text-white transition-all",
                canAdvance && !submitting
                  ? "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800"
                  : "bg-emerald-600 opacity-40 cursor-not-allowed",
              ].join(" ")}
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Submitting…
                </>
              ) : isLast ? (
                <>
                  Submit <ChevronRight className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Step 4: Done / Thank you
const DonePage = ({ survey, inviter, form, completionTimeInSeconds }) => (
  <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-950">
    <div className="min-h-full flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-sm flex flex-col items-center text-center">
        <div
          className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 
                    border-2 border-emerald-200 dark:border-emerald-500/30
                    flex items-center justify-center mb-6"
        >
          <CheckCircle2 className="h-8 w-8 text-emerald-500" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Thank you{form?.name ? `, ${form.name.split(" ")[0]}` : ""}!
        </h1>

        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md leading-relaxed">
          Your responses have been submitted.{" "}
          {inviter?.name
            ? `${inviter.name} will review them and may be in touch.`
            : "The team will review your responses."}
        </p>

        <div className="w-16 h-px bg-gray-200 dark:bg-gray-700 my-6" />

        {/* Summary card */}
        <div
          className="w-full bg-gray-50 dark:bg-gray-900 
                    rounded-2xl px-6 py-5 text-left space-y-2"
        >
          <p
            className="text-xs font-semibold text-gray-400 dark:text-gray-500 
                        uppercase tracking-widest mb-3"
          >
            Submitted to
          </p>
          {inviter?.name && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">{inviter.name}</span>
              {inviter.company && (
                <span className="text-gray-500 dark:text-gray-400">
                  {" "}
                  · {inviter.company}
                </span>
              )}
            </p>
          )}
          {inviter?.email && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {inviter.email}
            </p>
          )}
          <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-2 space-y-1.5">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Survey:{" "}
              <span className="text-gray-600 dark:text-gray-300">
                {survey?.name}
              </span>
            </p>
            {formatDuration(completionTimeInSeconds) && (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Completed in{" "}
                <span className="text-gray-600 dark:text-gray-300">
                  {formatDuration(completionTimeInSeconds)}
                </span>
              </p>
            )}
          </div>
        </div>

        <PoweredBy />
      </div>
    </div>
  </div>
);
const TakeSurveyPage = observer(() => {
  const theme = useTheme();
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email");
  const shareToken = searchParams.get("token");
  const isExternalRespondent = searchParams.get("allowExternalRespondents");
  const [step, setStep] = useState(STEP.LANDING);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [completionTimeInSeconds, setCompletionTimeInSeconds] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const store = useSurveyLibraryStore();
  const responseStore = useResponseSurveyStore();
  const { user } = useAuthStore();
  const INVITER = useMemo(
    () => ({
      id: user?.userId || "",
      name: user?.name || user?.fullName || "Unknown",
      company: user?.companyName || "",
      email: user?.email || "",
      companyId: user?.companyId || "",
    }),
    [user],
  );

  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const [form, setForm] = useState({
    name: "",
    email: prefillEmail ?? "",
    company: "",
    jobTitle: "",
  });
  // ✅ Store full survey object
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await store.fetchSurveyById(surveyId);
        setSurvey(data);
        setSelectedQuestions(data.questions || []);
      } catch (err) {
        console.error("Failed to load survey:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [surveyId]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevHeight = document.documentElement.style.height;
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    document.body.style.height = "100%";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.height = prevHeight;
      document.body.style.height = "";
    };
  }, []);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };
  const handleUploadFiles = async (files) => {
    if (!files?.length || !INVITER.companyId) return [];
    try {
      return await responseStore.uploadAttachment(INVITER.companyId, files);
    } catch {
      showToast.error("Upload failed");
      return [];
    }
  };
  const handleUploadBatchDone = (count) => {
    showToast.success(
      count === 1 ? "1 file uploaded" : `${count} files uploaded`,
    );
  };
  const handleDeleteFile = async (fileId) => {
    await responseStore.deleteAttachment(fileId);
  };
  const handleDownloadFile = async (file) => {
    await responseStore.downloadAttachment(file);
  };
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(
        ([questionId, answer]) => {
          const q = selectedQuestions.find((q) => q._id === questionId);
          return {
            questionId,
            questionText: q?.questionText ?? "",
            questionType: q?.questionType ?? "text",
            category: q?.category ?? "common",
            options: q?.options ?? [],
            answer,
          };
        },
      );

      const duration = startedAt
        ? Math.round((Date.now() - startedAt) / 1000)
        : null;

      setCompletionTimeInSeconds(duration);
      const payload = {
        surveyId,
        inviterId: INVITER?.id || null,
        inviterName: INVITER?.name || null,
        inviterEmail: INVITER?.email || null,
        shareToken,
        isExternalRespondent,
        respondent: {
          name: form.name,
          email: form.email,
          company: form.company,
          jobTitle: form.jobTitle,
        },
        answers: answersArray,
        submittedAt: new Date().toISOString(),
        completionTimeInSeconds: duration,
        metadata: {
          userAgent: navigator.userAgent,
          locale: navigator.language,
        },
      };
      await responseStore.submitResponse(payload);
      await new Promise((r) => setTimeout(r, 1200));
      setStep(STEP.DONE);
    } catch (err) {
      console.error("Submit Response failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  //loading state
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-gray-400">Loading survey...</p>
      </div>
    );
  }

  //not found
  if (!survey) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-red-400">Survey not found.</p>
      </div>
    );
  }
  //expired
  const isExpired = survey.expiresAt && new Date(survey.expiresAt) < new Date();
  if (isExpired) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white px-6">
        <div className="max-w-md w-full text-center">
          {/* Icon */}
          <div
            className="w-16 h-16 rounded-full bg-red-50 border-2 border-red-200
                            flex items-center justify-center mx-auto mb-6"
          >
            <span className="text-2xl">⏰</span>
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-2">
            This survey has expired
          </h1>

          <p className="text-sm text-gray-500 leading-relaxed mb-2">
            <strong>{survey.name}</strong> is no longer accepting responses.
          </p>

          {/* Expiry date */}
          <p className="text-xs text-gray-400">
            Expired on{" "}
            {new Date(survey.expiresAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>

          {/* Divider */}
          <div className="w-12 h-px bg-gray-200 mx-auto my-6" />

          {/* ✅ Inviter info */}
          {(INVITER?.name || INVITER?.company) && (
            <div className="bg-gray-50 rounded-xl px-5 py-4 text-left mb-4 space-y-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                Survey created by
              </p>
              {INVITER?.name && (
                <p className="text-sm font-medium text-gray-700">
                  {INVITER.name}
                </p>
              )}
              {INVITER?.company && (
                <p className="text-sm text-gray-500">{INVITER.company}</p>
              )}
              {INVITER?.email && (
                <a
                  href={`mailto:${INVITER.email}`}
                  className="text-sm text-emerald-600 hover:text-emerald-700
                                           font-medium transition-colors"
                >
                  {INVITER.email}
                </a>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            If you think this is a mistake, please{" "}
            {INVITER?.email ? (
              <a
                href={`mailto:${INVITER.email}`}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                contact {INVITER?.name?.split(" ")[0] || "the creator"}
              </a>
            ) : (
              <span>contact the survey creator</span>
            )}
            .
          </p>

          <PoweredBy />
        </div>
      </div>
    );
  }
  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden 
            bg-white dark:bg-gray-950"
    >
      <TopHeader isDark={theme.isDark} onToggleDark={theme.toggleTheme} />

      {/* ✅ Pass isDark and onToggleDark */}
      {step === STEP.LANDING && (
        <LandingPage
          survey={survey}
          shareLink={SHARE_LINK}
          inviter={INVITER}
          questions={selectedQuestions}
          onStart={() => {
            setStartedAt(Date.now());
            setStep(STEP.DETAILS);
          }}
        />
      )}

      {step === STEP.DETAILS && (
        <DetailsPage
          inviter={INVITER}
          email={prefillEmail}
          form={form}
          setForm={setForm}
          onBack={() => setStep(STEP.LANDING)}
          onContinue={() => setStep(STEP.QUESTION)}
        />
      )}

      {step === STEP.QUESTION && (
        <QuestionPage
          survey={survey}
          questions={selectedQuestions}
          answers={answers}
          onAnswer={handleAnswer}
          onBack={() => setStep(STEP.DETAILS)}
          onSubmit={handleSubmit}
          submitting={submitting}
          uploadFn={handleUploadFiles}
          deleteFn={handleDeleteFile}
          downloadFn={handleDownloadFile}
          uploadCompleteFn={handleUploadBatchDone}
        />
      )}

      {step === STEP.DONE && (
        <DonePage
          survey={survey}
          inviter={INVITER}
          form={form}
          completionTimeInSeconds={completionTimeInSeconds}
        />
      )}
    </div>
  );
});

export default TakeSurveyPage;
